import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { SelectBox } from '@tecuity/granite-ui/SelectBox'
import SelectableCheckboxList, {
  CheckboxList,
} from '../SettingsViews/SettingsComponents/SelectableCheckboxList'
import * as api from '../../../requests'
import '../SettingsViews/SettingsListView.scss'
import { setErrorToast, setSuccessToast } from '../../../actions'
import { useRequestData } from '../../../Utilities/hooks'
import { Spinner } from '../../Loaders/Loaders'
import { ContextButton } from '@tecuity/granite-ui/ContextButton'
import { Checkbox } from '@tecuity/granite-ui/Checkbox'
import { TextInput } from '@tecuity/granite-ui/TextInput'
import Revert from '@tecuity/sos-icons/react/revert'
import RightArrow from '@tecuity/sos-icons/react/right-arrow'
import format from 'date-fns/format'
import { DetailList } from '@tecuity/granite-ui'
import { getSourceTypeId } from '../../../Utilities/Utilities'

const BusinessFilingToRecordTypeXref = () => {
  const dispatch = useDispatch()
  const imagePageTypes = useRequestData(api.GET_ALL_IMAGE_PAGE_TYPES, [], [])
  const sourceTypes = useRequestData(api.GET_SOURCE_TYPE_OPTIONS, [], [])
  const recordAccessUserRoles = useRequestData(
    (cb, errCb) => {
      api.GET_RECORD_ACCESS_USER_ROLES_BY_SOURCE_TYPE(getSourceTypeId('business'), cb, errCb)
    },
    [],
    []
  )
  const formData = useRequestData(
    (cb, errCb) => api.GET_ALL_FORMS_BY_CATEGORY('Business', cb, errCb),
    [],
    []
  )
  const appReports = useRequestData(api.GET_APP_REPORTS, [], [])
  const businessStatusTypes = useRequestData(
    (cb, errCb) => api.GET_ACTIVE_BASIC_LOOKUP_TYPES('Business', 'StatusType', cb, errCb),
    [],
    []
  )
  const standingTypes = useRequestData(
    (cb, errCb) => api.GET_ACTIVE_BASIC_LOOKUP_TYPES('', 'StandingType', cb, errCb),
    [],
    []
  )
  const durationTermTypes = useRequestData(
    (cb, errCb) => api.GET_ACTIVE_BASIC_LOOKUP_TYPES('', 'DurationTermType', cb, errCb),
    [],
    []
  )
  const expirationReferenceTypes = useRequestData(
    (cb, errCb) => api.GET_ACTIVE_BASIC_LOOKUP_TYPES('', 'ExpirationReferenceType', cb, errCb),
    [],
    []
  )
  const [recordTypeOptions, setRecordTypeOptions] = useState([])
  const [recordSubTypeOptions, setRecordSubTypeOptions] = useState([])
  const [recordSubTypeXref, setRecordSubTypeXref] = useState([])
  const [filingTypeList, setFilingTypeList] = useState([])
  const [recordType, setRecordType] = useState(0)
  const [recordSubType, setRecordSubType] = useState(0)
  const [activeFilingId, setActiveFilingId] = useState(0)
  const [loadingReceiptItemTypes, setLoadingReceiptItemTypes] = useState(false)
  const [receiptItemTypeIds, setReceiptItemTypeIds] = useState([])
  const [receiptItemTypes, setReceiptItemTypes] = useState([])
  const [originalIds, setOriginalIds] = useState([])
  const [checkedFilingTypeIds, setCheckedFilingTypeIds] = useState([])
  const [xRef, setXRef] = useState({ ...defaultDto })
  const [xRefMap] = useState({})
  const [loadingXref, setLoadingXref] = useState(false)
  const [isDirty, setIsDirty] = useState(false)

  useEffect(() => {
    getLookupTypeOptions()
  }, [])

  const getLookupTypeOptions = () => {
    api.GET_ACTIVE_BASIC_LOOKUP_TYPES(
      'Business',
      'RecordType',
      (res) => {
        res.sort((a, b) => (a.Name > b.Name ? 1 : b.Name > a.Name ? -1 : 0))
        setRecordTypeOptions([
          ...res.map((t) => ({
            value: t.Id,
            label: t.Name,
          })),
        ])
        if (!recordType && res.length > 0) setRecordType(res[0].Id)
      },
      (err) => {
        setRecordTypeOptions([])
        console.error(err.message)
      }
    )
    api.GET_ACTIVE_BASIC_LOOKUP_TYPES(
      'Business',
      'RecordSubType',
      (res) => {
        res.sort((a, b) => (a.Name > b.Name ? 1 : b.Name > a.Name ? -1 : 0))
        setRecordSubTypeOptions([
          { value: '0', label: 'None' },
          ...res.map((t) => ({
            value: t.Id,
            label: t.Name,
          })),
        ])
        // if (!recordType && res.length > 0) setRecordType(res[0].Id)
      },
      (err) => {
        setRecordSubTypeOptions([{ value: '0', label: 'None' }])
        console.error(err.message)
      }
    )
    api.GET_ACTIVE_BASIC_LOOKUP_TYPES(
      'Business',
      'FilingType',
      (res) => {
        res.sort((a, b) => (a.Name > b.Name ? 1 : b.Name > a.Name ? -1 : 0))
        setFilingTypeList(res)
      },
      (err) => {
        setFilingTypeList([])
        console.error(err.message)
      }
    )
  }

  const getBusinessFilingTypeIdsByRecordTypeSubType = (controller) => {
    if (recordType) {
      api.GET_SETTINGS_BUSINESS_FILING_TYPE_IDS_BY_RECORD_TYPE_SUB_TYPE(
        { signal: controller.signal },
        recordType,
        recordSubType,
        (res) => {
          if (res) {
            setCheckedFilingTypeIds(res)
            setOriginalIds(res)
          }
        },
        (err) => {
          console.error(err)
          dispatch(setErrorToast('Failed to load selected Filing Type Ids'))
        }
      )
    }
  }

  useEffect(() => {
    const controller = new AbortController()
    api.GET_BUSINESS_RECORD_SUB_TYPE_XREFS_BY_RECORD_TYPE(
      recordType,
      (res) => {
        // console.log(res)
        setRecordSubTypeXref(res.BusinessRecordSubTypeIds)
      },
      (err) => {
        console.error('retrieving', err)
        setRecordSubTypeXref([])
      }
    )
    return () => {
      controller.abort()
    }
  }, [recordType])

  useEffect(() => {
    const controller = new AbortController()
    getBusinessFilingTypeIdsByRecordTypeSubType(controller)
    return () => {
      controller.abort()
    }
  }, [recordType, recordSubType])

  const getXrefByRecordTypeFilingType = (controller) => {
    if (recordType && activeFilingId) {
      setLoadingXref(true)
      api.GET_SETTINGS_BUSINESS_FILING_TO_RECORD_XREF(
        { signal: controller.signal },
        recordType,
        recordSubType,
        activeFilingId,
        (res) => {
          if (isDirty && Object.keys(xRefMap).includes(activeFilingId.toString())) {
            setXRef({ ...xRefMap[activeFilingId] })
          } else if (res) {
            setXRef({
              ...res,
              // AppReportIds: res.ReportConfigJson ? JSON.parse(res.ReportConfigJson) : [],
            })
          } else {
            setXRef({
              ...defaultDto,
              BusinessRecordTypeId: recordType,
              BusinessRecordSubTypeId: recordSubType,
              BusinessFilingTypeId: activeFilingId,
            })
          }
          setLoadingXref(false)
        },
        (err) => {
          if (JSON.parse(err).status === 404) {
            setXRef({
              ...defaultDto,
              BusinessRecordTypeId: recordType,
              BusinessRecordSubTypeId: recordSubType,
              BusinessFilingTypeId: activeFilingId,
            })
          } else {
            console.error(err)
            if (JSON.parse(err).status !== 404) dispatch(setErrorToast('Failed to load Xref'))
          }
          setLoadingXref(false)
        }
      )
    }
  }

  useEffect(() => {
    const controller = new AbortController()
    getXrefByRecordTypeFilingType(controller)
    return () => {
      controller.abort()
    }
  }, [recordType, activeFilingId])

  const clickRevert = () => {
    Object.keys(xRefMap).forEach((key) => {
      delete xRefMap[key]
    })
    setIsDirty(false)
    getBusinessFilingTypeIdsByRecordTypeSubType(new AbortController())
    getXrefByRecordTypeFilingType(new AbortController())
    getLookupTypeOptions()
  }

  const clickSave = () => {
    const promiseArray = []
    delete xRefMap['0']
    const changedFilingTypeIds = Object.keys(xRefMap)

    changedFilingTypeIds.forEach((filingTypeId) => {
      if (originalIds.includes(+filingTypeId)) {
        if (checkedFilingTypeIds.includes(+filingTypeId)) {
          //update the existing
          promiseArray.push(
            new Promise((resolve, reject) => {
              api.SAVE_SETTINGS_BUSINESS_FILING_TYPE_RECORD_TYPE_XREF(
                'PUT',
                {
                  ...xRefMap[filingTypeId],
                  BusinessRecordSubTypeId:
                    xRefMap[filingTypeId].BusinessRecordSubTypeId === 0
                      ? null
                      : xRefMap[filingTypeId].BusinessRecordSubTypeId,
                },
                resolve,
                reject
              )
            })
          )
        } else {
          // delete the existing
          promiseArray.push(
            new Promise((resolve, reject) => {
              api.DELETE_SETTINGS_BUSINESS_FILING_TYPE_RECORD_TYPE_XREF(
                xRefMap[filingTypeId].BusinessFilingTypeXrefId,
                resolve,
                reject
              )
            })
          )
        }
      } else if (checkedFilingTypeIds.includes(+filingTypeId)) {
        // add new xref
        promiseArray.push(
          new Promise((resolve, reject) => {
            api.SAVE_SETTINGS_BUSINESS_FILING_TYPE_RECORD_TYPE_XREF(
              'POST',
              {
                ...xRefMap[filingTypeId],
                BusinessRecordSubTypeId:
                  xRefMap[filingTypeId].BusinessRecordSubTypeId === 0
                    ? null
                    : xRefMap[filingTypeId].BusinessRecordSubTypeId,
              },
              resolve,
              reject
            )
          })
        )
      } else {
        // nothing changed
        console.warn('Business Xref data out of sync. This should NOT happen.')
      }
    })

    if (promiseArray.length > 0) {
      setLoadingXref(true)
      Promise.all(promiseArray)
        .then((xrefs) => {
          // console.log(xrefs)
          dispatch(setSuccessToast('Saved Setting(s) successfully.'))
          clickRevert()
          setLoadingXref(false)
        })
        .catch((err) => {
          if (err.DisplayToUser === true && (err.Message || '') !== '') {
            dispatch(setErrorToast(err.Message))
          } else {
            dispatch(setErrorToast('Failed to save setting.'))
          }
          setLoadingXref(false)
        })
    }
  }

  const disableXrefSettings = useMemo(() => {
    if (loadingXref) return true
    return !checkedFilingTypeIds.includes(activeFilingId)
  }, [activeFilingId, checkedFilingTypeIds, loadingXref])

  const changeXrefValue = (property, value) => {
    setXRef({ ...xRef, [property]: value })
    setIsDirty(true)
  }

  useEffect(() => {
    xRefMap[xRef.BusinessFilingTypeId] = { ...xRef }
  }, [xRef])

  useEffect(() => {
    const businessSourceType = sourceTypes.find((type) => type.ModuleName === 'Business')
    const controller = new AbortController()
    if (businessSourceType) {
      setLoadingReceiptItemTypes(true)
      api.GET_SETTINGS_RECEIPT_ITEM_TYPE_XREF_BY_TYPES_SUB_TYPE(
        businessSourceType.Id,
        recordType,
        activeFilingId,
        recordSubType,
        (res) => {
          setReceiptItemTypeIds(res.ReceiptItemTypeIds)
        },
        (err) => {
          console.error('retrieving', err)
          setReceiptItemTypeIds([])
        }
      )
    }
    return () => {
      controller.abort()
    }
  }, [activeFilingId, recordType, recordSubType, sourceTypes])

  useEffect(() => {
    const promiseArray = []

    receiptItemTypeIds.forEach((id) => {
      promiseArray.push(
        new Promise((resolve, reject) => {
          api.GET_SETTINGS_RECEIPT_ITEM_TYPE_BY_ID(id, resolve, reject)
        })
      )
    })

    if (promiseArray.length === 0) setLoadingReceiptItemTypes(false)

    Promise.all(promiseArray)
      .then((res) => {
        const array = []
        res.forEach((type) => {
          array.push(type)
        })
        setReceiptItemTypes(array)
        setLoadingReceiptItemTypes(false)
      })
      .catch((err) => {
        console.error(err)
      })
  }, [receiptItemTypeIds])

  return (
    <div className="settings-list-view">
      <h2>Business Filing to Record Type</h2>
      <div className="settings-content">
        <div className="settings-left-column">
          <div className="custom-correspondence-options">
            <SelectBox
              label="Record Type"
              noPlaceholder
              value={recordType || ''}
              onChange={(val) => {
                setRecordType(+val)
                setActiveFilingId(0)
              }}
              options={recordTypeOptions}
              disabled={isDirty ? true : undefined}
              description={isDirty ? 'Save or Revert changes to modify.' : undefined}
            />
            <SelectBox
              label="Record Sub Type"
              noPlaceholder
              value={recordSubType || ''}
              onChange={(val) => {
                setRecordSubType(+val)
                setActiveFilingId(0)
              }}
              options={(recordSubTypeOptions || []).filter(
                (opt) => +opt.value === 0 || (recordSubTypeXref || []).includes(+opt.value)
              )}
              disabled={isDirty ? true : undefined}
              description={isDirty ? 'Save or Revert changes to modify.' : undefined}
            />
          </div>
          <SelectableCheckboxList
            label={`Filing Types`}
            items={filingTypeList.map((type) => {
              return {
                id: type.Id,
                label: type.Name,
              }
            })}
            activeId={activeFilingId}
            setActiveId={setActiveFilingId}
            checked={checkedFilingTypeIds}
            setChecked={(ids) => {
              setCheckedFilingTypeIds(ids)
              setIsDirty(true)
            }}
          />
        </div>
        <div className="properties-list scrollable">
          {/* DETAILS */}
          <h3
            style={{
              textDecoration: 'underline',
              display: 'flex',
              alignSelf: 'center',
              fontWeight: 'bold',
            }}
          >
            Details
          </h3>

          <SelectBox
            label="Image Page Type"
            options={imagePageTypes
              .map((imagePageType) => {
                return {
                  label: imagePageType.Name,
                  value: imagePageType.Id,
                }
              })
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('ImagePageTypeId', +value)
            }}
            value={xRef.ImagePageTypeId || ''}
            disabled={disableXrefSettings}
          />

          <SelectBox
            label="Form Title"
            options={formData
              .filter((form) => {
                return form.IsActive
              })
              .map((form) => {
                return {
                  label: form.Title,
                  value: form.FormDefinitionId,
                }
              })
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('FormDefinitionId', +value)
            }}
            value={xRef.FormDefinitionId || ''}
            disabled={disableXrefSettings}
          />

          <div className="property-readonly">
            <label>{`Receipt Item(s) / Fee`}</label>
          </div>
          {loadingReceiptItemTypes && (
            <div className="inline-spinner">
              <Spinner size="small" themeColor="dark-neutral" />
            </div>
          )}
          {!loadingReceiptItemTypes && (
            <DetailList
              topBorder
              bottomBorder
              details={
                receiptItemTypes.length > 0
                  ? receiptItemTypes.map((type) => {
                      const fee =
                        type.Fee === 'SQL Query'
                          ? type.Fee
                          : `$${Number(type.Fee)
                              .toFixed(2)
                              .toString()
                              .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
                      return { label: type.Name, value: fee }
                    })
                  : [
                      {
                        label: 'No Receipt Items',
                        value: `Configure in 'Receipt Item to Filing Types' Fiscal Setting`,
                      },
                    ]
              }
            />
          )}

          {xRef.LastModifiedDate && (
            <div className="property-readonly">
              <label>
                {'Last Modified Date'}
                :&nbsp;
              </label>
              <div>{format(new Date(xRef.LastModifiedDate), 'M/dd/yyyy hh:mm aa')}</div>
            </div>
          )}

          {xRef.LastModifiedByName && (
            <div className="property-readonly">
              <label>
                {'Last Modified By'}
                :&nbsp;
              </label>
              <div>{xRef.LastModifiedByName}</div>
            </div>
          )}
          {xRef.BusinessFilingTypeXrefId && (
            <div className="property-readonly">
              <label>
                {'Xref Id'}
                :&nbsp;
              </label>
              <div>{xRef.BusinessFilingTypeXrefId}</div>
            </div>
          )}
          {/* REQUIREMENTS FOR FILING */}
          <h3
            style={{
              textDecoration: 'underline',
              display: 'flex',
              alignSelf: 'center',
              fontWeight: 'bold',
            }}
          >
            Requirements to File
          </h3>

          <CheckboxList
            label="Record Access Roles Permitted to File (Leave blank to allow open filing)"
            items={recordAccessUserRoles
              .filter((x) => x.CanFileAmendments || x.CanFileSpecialAmendments)
              .map((role) => ({
                id: role.RecordAccessUserRoleId,
                label: role.Name,
              }))
              .sort((a, b) => (a.label > b.label ? 1 : -1))}
            checkedIds={xRef.PermittedRecordAccessUserRoleIds || []}
            setChecked={(ids) => {
              changeXrefValue('PermittedRecordAccessUserRoleIds', ids)
            }}
            disabled={disableXrefSettings}
          />

          <CheckboxList
            label={'Status to File'}
            items={businessStatusTypes
              .map((type) => ({ id: type.Id, label: type.Name }))
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            checkedIds={xRef.PriorBusinessStatusIds || []}
            setChecked={(ids) => {
              changeXrefValue('PriorBusinessStatusIds', ids)
            }}
            disabled={disableXrefSettings}
          />

          <TextInput
            label={`Must File Within X Months of Formation`}
            value={xRef.MustFileWithinMonths || ''}
            onChange={(e) => {
              changeXrefValue('MustFileWithinMonths', e.target.value === '' ? null : e.target.value)
            }}
            disabled={disableXrefSettings}
            type="number"
          />

          <TextInput
            label={`Maximum Number of Days Before Expiration this can be Filed`}
            value={xRef.MaxDaysBeforeExpiration || ''}
            onChange={(e) => {
              changeXrefValue(
                'MaxDaysBeforeExpiration',
                e.target.value === '' ? null : e.target.value
              )
            }}
            disabled={disableXrefSettings}
            type="number"
          />

          <TextInput
            label={`Maximum Number of Days Before Annual Report Due Date this can be Filed`}
            value={xRef.MaxDaysBeforeARDueDate || ''}
            onChange={(e) => {
              changeXrefValue('MaxDaysBeforeARDueDate', e.target.value === '' ? null : e.target.value)
            }}
            disabled={disableXrefSettings}
            type="number"
          />

          <SelectBox
            label="Annual Report Standing to File"
            options={standingTypes
              .map((cd) => {
                return {
                  label: cd.Name,
                  value: cd.Id,
                }
              })
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('PriorARStanding', +value)
            }}
            value={xRef.PriorARStanding || '0'}
            disabled={disableXrefSettings}
            noPlaceholder
          />

          <SelectBox
            label="Agent Standing to File"
            options={standingTypes
              .map((cd) => {
                return {
                  label: cd.Name,
                  value: cd.Id,
                }
              })
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('PriorAgentStanding', +value)
            }}
            value={xRef.PriorAgentStanding || '0'}
            disabled={disableXrefSettings}
            noPlaceholder
          />

          <SelectBox
            label="Other Standing to File"
            options={standingTypes
              .map((cd) => {
                return {
                  label: cd.Name,
                  value: cd.Id,
                }
              })
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('PriorOtherStanding', +value)
            }}
            value={xRef.PriorOtherStanding || '0'}
            disabled={disableXrefSettings}
            noPlaceholder
          />
          <Checkbox
            label="Only Allow One Pending Filing Per User Per Record at a time"
            checked={xRef.LimitOnePendingFilingPerRecord}
            onChange={(checked) => {
              changeXrefValue('LimitOnePendingFilingPerRecord', checked)
            }}
            disabled={disableXrefSettings}
          />

          {/* AFTER APPROVAL */}
          <h3
            style={{
              textDecoration: 'underline',
              display: 'flex',
              alignSelf: 'center',
              fontWeight: 'bold',
            }}
          >
            Results of Approved Filing
          </h3>

          <SelectBox
            label="Record Access Role Granted Post-Approval"
            options={recordAccessUserRoles
              .map((role) => {
                return {
                  label: role.Name,
                  value: role.RecordAccessUserRoleId,
                }
              })
              .sort((a, b) => {
                return a.value > b.value ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue(
                'NewRecordAccessUserRoleId',
                value === '' ? null : +value
              )
            }}
            value={xRef.NewRecordAccessUserRoleId || ''}
            disabled={disableXrefSettings}
          />

          <SelectBox
            label="Business Status After Filing"
            options={[...businessStatusTypes, { Name: 'No Change', Id: -1 }]
              .map((cd) => {
                return {
                  label: cd.Name,
                  value: cd.Id,
                }
              })
              .sort((a, b) => {
                return a.label === 'No Change' ? -1 : a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('NewBusinessStatusTypeId', +value)
            }}
            value={xRef.NewBusinessStatusTypeId || -1}
            disabled={disableXrefSettings}
            noPlaceholder
          />

          <SelectBox
            label="Annual Report Standing After Filing"
            options={standingTypes
              .map((cd) => {
                return {
                  label: cd.Name,
                  value: cd.Id,
                }
              })
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('NewARStanding', +value)
            }}
            value={xRef.NewARStanding || '0'}
            disabled={disableXrefSettings}
            noPlaceholder
          />

          <SelectBox
            label="Agent Standing After Filing"
            options={standingTypes
              .map((cd) => {
                return {
                  label: cd.Name,
                  value: cd.Id,
                }
              })
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('NewAgentStanding', +value)
            }}
            value={xRef.NewAgentStanding || '0'}
            disabled={disableXrefSettings}
            noPlaceholder
          />

          <SelectBox
            label="Other Standing After Filing"
            options={standingTypes
              .map((cd) => {
                return {
                  label: cd.Name,
                  value: cd.Id,
                }
              })
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('NewOtherStanding', +value)
            }}
            value={xRef.NewOtherStanding || '0'}
            disabled={disableXrefSettings}
            noPlaceholder
          />
          <Checkbox
            label="Set Agent to SOS"
            checked={xRef.SetAgentToSOS}
            onChange={(checked) => {
              changeXrefValue('SetAgentToSOS', checked)
            }}
            disabled={disableXrefSettings}
          />
          <Checkbox
            label="Set Agent to No Agent"
            checked={xRef.SetAgentToNoAgent}
            onChange={(checked) => {
              changeXrefValue('SetAgentToNoAgent', checked)
            }}
            disabled={disableXrefSettings}
          />

          <SelectBox
            label="Duration Term"
            options={durationTermTypes
              .map((cd) => {
                return {
                  label: cd.Name,
                  value: cd.Id,
                }
              })
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('DurationTermType', +value)
            }}
            value={xRef.DurationTermType || '0'}
            disabled={disableXrefSettings}
          />

          <SelectBox
            label="Expiration Reference"
            options={expirationReferenceTypes
              .map((cd) => {
                return {
                  label: cd.Name,
                  value: cd.Id,
                }
              })
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('ExpirationReference', +value)
            }}
            value={xRef.ExpirationReference || '0'}
            disabled={disableXrefSettings}
          />

          <Checkbox
            label="Include Original Image with Approval Stamp for Paper Filings"
            checked={xRef.IncludeImageForPaperFiling || false}
            onChange={(checked) => {
              changeXrefValue('IncludeImageForPaperFiling', checked)
            }}
            disabled={disableXrefSettings}
          />

          <CheckboxList
            label={'Generated Reports'}
            items={appReports
              .map((appReport) => ({ id: appReport.Id, label: appReport.Name }))
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            checkedIds={xRef.AppReportIds || []}
            setChecked={(ids) => {
              changeXrefValue('AppReportIds', ids)
            }}
            disabled={disableXrefSettings}
          />
          <div className="button-wrapper">
            {loadingXref && (
              <div className="loading-type">
                <Spinner size="small" themeColor="dark-neutral" />
              </div>
            )}
            <ContextButton
              variant="danger"
              disabled={!isDirty}
              leftIcon={
                <div className="checkmark-wrapper left">
                  <Revert />
                </div>
              }
              onClick={clickRevert}
            >
              Revert
            </ContextButton>
            <ContextButton
              variant="primary"
              disabled={!isDirty}
              rightIcon={
                <div className="checkmark-wrapper right">
                  <RightArrow />
                </div>
              }
              onClick={clickSave}
            >
              Save
            </ContextButton>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BusinessFilingToRecordTypeXref

//dto stuff
// public int BusinessFilingTypeId { get; set; }
// public int BusinessRecordTypeId { get; set; }
// public int BusinessRecordSubTypeId { get; set; }
// public int ReceiptItemTypeId { get; set; }
// public List<int> PriorBusinessStatusIds { get; set; }
// public int PriorARStanding { get; set; }
// public int PriorAgentStanding { get; set; }
// public int PriorOtherStanding { get; set; }
// public int? NewBusinessStatusTypeId { get; set; }
// public int NewARStanding { get; set; }
// public int NewAgentStanding { get; set; }
// public int NewOtherStanding { get; set; }
// public int? FormDefinitionId { get; set; }
// public List<int> AppReportIds { get; set; }
// public bool SetAgentToSOS { get; set; }
// public bool SetAgentToNoAgent { get; set; }
// public int? MustFileWithinMonths { get; set; }
// public int? ImageDataTypeId { get; set; } // in
// public int? RequiredRecordOwnerTypeId { get; set; } // in
// public int? NewRecordOwnerTypeId { get; set; } // in

const defaultDto = {
  BusinessFilingTypeId: 0, //done
  BusinessRecordTypeId: 0, //done
  BusinessRecordSubTypeId: null, //done
  FormDefinitionId: null, //done
  IncludeImageForPaperFiling: false,
  AppReportIds: [], // done
  ImagePageTypeId: null, //done
  // RequiredRecordOwnerTypeId: null, //done
  // NewRecordOwnerTypeId: null, //done
  PermittedRecordAccessUserRoleIds: [],
  NewRecordAccessUserRoleId: null,
  PriorBusinessStatusIds: [], //done
  PriorARStanding: 0, // done
  PriorAgentStanding: 0, //done
  PriorOtherStanding: 0, //done
  NewBusinessStatusTypeId: 0, //done
  NewARStanding: 0, //done
  NewAgentStanding: 0, //done
  NewOtherStanding: 0, // done
  SetAgentToSOS: false, //done
  SetAgentToNoAgent: false, //done
  MustFileWithinMonths: 0, //done
  MaxDaysBeforeExpiration: 0,
  MaxDaysBeforeARDueDate: null,
  DurationTermType: 0, //done
  ExpirationReference: 0, //done
  LastModifiedDate: new Date(), //done
  LastModifiedByName: '', //done
  limitOnePendingFilingPerRecord: false,
}
